import { deploymentUsageItems, isLegacyBundleAsset, operationalAssets } from './deploymentUsageModel';
import { normalizeAssetId } from './trackingModel';

const DAY_MS = 24 * 60 * 60 * 1000;

function dayStamp(value, now = new Date()) {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (dateOnly) return Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

export function deploymentDayCount(deployment, now = new Date()) {
  const start = dayStamp(deployment?.startDate, now);
  if (start === null) return 0;
  const endValue = deployment?.status === 'closed' && deployment?.returnedAt ? deployment.returnedAt : now;
  const end = dayStamp(endValue, now);
  if (end === null) return 0;
  return Math.max(1, Math.floor((end - start) / DAY_MS) + 1);
}

export function deploymentYear(deployment) {
  const match = /^(\d{4})/.exec(String(deployment?.startDate || '').trim());
  if (match) return Number(match[1]);
  const date = new Date(deployment?.startDate || '');
  return Number.isNaN(date.getTime()) ? 0 : date.getFullYear();
}

export function availableSeasonYears(state, now = new Date()) {
  const years = new Set((Array.isArray(state?.deployments) ? state.deployments : []).map(deploymentYear).filter(Boolean));
  years.add(now.getFullYear());
  return [...years].sort((a, b) => b - a);
}

export function buildSeasonSummary(state, year, now = new Date()) {
  const selectedYear = Number(year) || now.getFullYear();
  const deployments = (Array.isArray(state?.deployments) ? state.deployments : []).filter(item => deploymentYear(item) === selectedYear);
  const members = new Map();
  const incidents = new Map();
  const gear = new Map();
  const supplies = new Map();
  const types = new Map();
  let personnelDays = 0;
  let longestDays = 0;

  deployments.forEach(deployment => {
    const days = deploymentDayCount(deployment, now);
    personnelDays += days;
    longestDays = Math.max(longestDays, days);

    const person = String(deployment?.person || 'Unknown').trim() || 'Unknown';
    const member = members.get(person) || { person, deployments: 0, days: 0 };
    member.deployments += 1;
    member.days += days;
    members.set(person, member);

    const incident = String(deployment?.incident || 'Unknown incident').trim() || 'Unknown incident';
    const incidentRow = incidents.get(incident) || { incident, deployments: 0, days: 0 };
    incidentRow.deployments += 1;
    incidentRow.days += days;
    incidents.set(incident, incidentRow);

    const type = String(deployment?.deploymentType || deployment?.role || 'EMPF').trim() || 'EMPF';
    types.set(type, (types.get(type) || 0) + 1);

    (deployment?.assetIds || []).forEach(rawId => {
      const assetId = normalizeAssetId(rawId);
      const asset = (state?.assets || []).find(item => normalizeAssetId(item?.id) === assetId);
      if (!assetId || isLegacyBundleAsset(asset || assetId)) return;
      const current = gear.get(assetId) || {
        assetId,
        assetName: String(asset?.name || assetId),
        category: String(asset?.category || 'Other'),
        deployments: 0,
        days: 0,
      };
      current.deployments += 1;
      current.days += days;
      gear.set(assetId, current);
    });

    deploymentUsageItems(state, deployment.id).forEach(item => {
      const key = `${item.catalogId}|${item.item}`;
      const current = supplies.get(key) || { catalogId: item.catalogId, item: item.item, quantity: 0 };
      current.quantity += Number(item.quantity || 0);
      supplies.set(key, current);
    });
  });

  return {
    year: selectedYear,
    deploymentCount: deployments.length,
    uniqueMembers: members.size,
    incidentCount: incidents.size,
    personnelDays,
    averageDays: deployments.length ? personnelDays / deployments.length : 0,
    longestDays,
    activeCount: deployments.filter(item => item.status !== 'closed').length,
    typeCounts: [...types.entries()].map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)),
    members: [...members.values()].sort((a, b) => b.days - a.days || b.deployments - a.deployments || a.person.localeCompare(b.person)),
    incidents: [...incidents.values()].sort((a, b) => b.days - a.days || b.deployments - a.deployments || a.incident.localeCompare(b.incident)),
    gear: [...gear.values()].sort((a, b) => b.days - a.days || b.deployments - a.deployments || a.assetName.localeCompare(b.assetName)),
    supplies: [...supplies.values()].filter(item => item.quantity > 0).sort((a, b) => b.quantity - a.quantity || a.item.localeCompare(b.item)),
    deployments: [...deployments].sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || ''))),
  };
}

export function buildGearStatus(state) {
  const assets = operationalAssets(state);
  const activeById = new Map((state?.deployments || []).filter(item => item.status !== 'closed').map(item => [item.id, item]));
  const groups = new Map();

  assets.forEach(asset => {
    const category = String(asset?.category || 'Other').trim() || 'Other';
    const deployment = asset?.currentDeploymentId ? activeById.get(asset.currentDeploymentId) || null : null;
    const row = {
      ...asset,
      deployment,
      statusLabel: asset.status === 'needs_inventory'
        ? 'Need Inventory'
        : asset.status === 'needs_restock'
          ? 'Needs Restock'
          : asset.status === 'out_of_service'
            ? 'OOS'
            : asset.status === 'deployed'
              ? 'Deployed'
              : asset.status === 'damaged'
                ? 'Damaged'
                : 'Available',
    };
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(row);
  });

  return [...groups.entries()]
    .map(([category, rows]) => ({ category, rows: rows.sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => a.category.localeCompare(b.category));
}
